import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
const defaultCounters = {
    sideDrawerCount: 0,
    floatingPopupCount: 0,
};
const defaultValue = {
    registerSideDrawer: () => () => undefined,
    registerFloatingPopup: () => () => undefined,
    counters: defaultCounters,
};
const SideDrawerRegistryContext = React.createContext(defaultValue);
/**
 * Tracks how many right-side drawers and floating popups are currently open across the app.
 *
 * Two independent counters let the Drawer and Sidekick coordinate:
 * - Drawer auto-registers as a side drawer so Sidekick can minimize into a floating popup.
 * - Sidekick's popup registers itself so the Drawer can relax its focus trap only while a
 *   floating surface needs to take focus (keeping the default focus enforcement everywhere else).
 *
 * Counters (rather than booleans) let multiple instances coexist and survive StrictMode's
 * double-invoke of effects.
 */
export const SideDrawerRegistryProvider = ({ children }) => {
    const [counters, setCounters] = React.useState(defaultCounters);
    const registerSideDrawer = React.useCallback(() => {
        setCounters(prev => ({ ...prev, sideDrawerCount: prev.sideDrawerCount + 1 }));
        return () => setCounters(prev => ({ ...prev, sideDrawerCount: Math.max(0, prev.sideDrawerCount - 1) }));
    }, []);
    const registerFloatingPopup = React.useCallback(() => {
        setCounters(prev => ({ ...prev, floatingPopupCount: prev.floatingPopupCount + 1 }));
        return () => setCounters(prev => ({ ...prev, floatingPopupCount: Math.max(0, prev.floatingPopupCount - 1) }));
    }, []);
    const value = React.useMemo(() => ({ registerSideDrawer, registerFloatingPopup, counters }), [registerSideDrawer, registerFloatingPopup, counters]);
    return _jsx(SideDrawerRegistryContext, { value: value, children: children });
};
/**
 * Registers the calling drawer as "open" while `isOpen` is true.
 *
 * Safe to call outside the provider — it becomes a no-op (useful for tests
 * and storybook that mount drawers in isolation).
 */
export const useRegisterSideDrawer = (isOpen) => {
    const { registerSideDrawer } = React.use(SideDrawerRegistryContext);
    React.useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        return registerSideDrawer();
    }, [isOpen, registerSideDrawer]);
};
/**
 * Returns true when at least one right-side drawer is open.
 */
export const useIsSideDrawerOpen = () => {
    const { counters } = React.use(SideDrawerRegistryContext);
    return counters.sideDrawerCount > 0;
};
/**
 * Registers the calling floating popup as "open" while `isOpen` is true.
 *
 * Safe to call outside the provider — no-op fallback.
 */
export const useRegisterFloatingPopup = (isOpen) => {
    const { registerFloatingPopup } = React.use(SideDrawerRegistryContext);
    React.useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        return registerFloatingPopup();
    }, [isOpen, registerFloatingPopup]);
};
/**
 * Returns true when at least one floating popup is open.
 *
 * Consumed by `Drawer` to drop its focus trap only while a floating surface
 * needs to accept focus — so all other drawer usages keep the default behaviour.
 */
export const useIsFloatingPopupOpen = () => {
    const { counters } = React.use(SideDrawerRegistryContext);
    return counters.floatingPopupCount > 0;
};
